/**
 * Left-nav counts for Intake / Transitions / Renewals (ADO #1597).
 *
 * Mounted by OpsRootLayout, so these run on every Ops page. Each count reads
 * through the SAME React Query keys its page uses (intake queue composition,
 * active imp projects, renewal queue membership), so the badge and an open
 * page share one fetch and cannot disagree. A count is undefined until its
 * data resolves; the nav simply renders no badge, so nothing flashes 0.
 */

import { useAuth } from '@shared/providers';
import { can } from '@shared/auth';
import { useIntakeQueue } from '@ops/pages/intake/ui/useIntakeQueue';
import { useImpProjects, ProjectStatus } from '@entities/imp-project';
import { useOpsGroups } from '@entities/ops-group';
import { useContractYears } from '@entities/contract-year';
import { useApprovalsCount } from '@entities/plan-year-financial';
import { countRenewalQueue } from '@ops/pages/renewals/lib/renewalQueue';

export interface OpsNavCounts {
  intake?: number;
  transitions?: number;
  renewals?: number;
  approvals?: number;
}

export function useOpsNavCounts(): OpsNavCounts {
  const intake = useIntakeQueue();
  const { roles } = useAuth();
  const { data: projects } = useImpProjects({ status: ProjectStatus.ACTIVE });
  const { data: groups } = useOpsGroups();
  const { data: years } = useContractYears({});
  // The BFF route is approver-gated, so the query only runs for approvers;
  // everyone else keeps undefined and the nav item stays hidden.
  const { data: approvals } = useApprovalsCount(can('adminfee.approve', roles ?? []));

  return {
    // The intake working set: opportunities still needing a decision. Enrolled,
    // linked, and dismissed rows are resolved and stay out of the badge.
    intake: intake.isLoading ? undefined : intake.newCount + intake.inReviewCount,
    transitions: projects?.length,
    // Past due + 0-30 only, not the whole queue — see countRenewalQueue.
    renewals: groups && years ? countRenewalQueue(groups, years) : undefined,
    approvals,
  };
}
